import {ValidationError} from './independentAgent';
import {Async, Empty} from 'Core/models/Async';
import {Email, ValidEmail} from 'Core/models/Email';
import {EmptyString} from 'Core/models/EmptyString';
import {Password, UnvalidatedPassword, ValidPassword} from 'Core/models/Password';
import {PhoneNumber, ValidPhoneNumber} from 'Core/models/PhoneNumber';
import {Either} from 'fp-ts/lib/Either';
import {createAction} from 'Shared/utilities/actionCreator';
import {
  UserSecurityQuestionForm,
  UserSecurityQuestions,
} from 'Users/components/UserProfile/UserSecurityQuestionsSection';
import {NameInformation, UnchangedNameInformation, ValidNameInformation} from 'Users/models/NameInformation';
import {NewEmail, UnvalidatedNewEmail} from 'Users/models/NewEmail';
import {NewUserName, UnvalidatedNewUserName, ValidNewUserName} from 'Users/models/NewUserName';
import {SecurityQuestionPayload} from 'Users/models/SecurityQuestionPayload';
import {UserProfilePayload} from 'Users/models/UserProfilePayload';

export enum UserAccountActionType {
  CHANGE_NAME_INFORMATION = 'userAccounts/CHANGE_NAME_INFORMATION',
  UPDATE_LOGIN_NAME = 'userAccounts/UPDATE_LOGIN_NAME',
  UPDATE_EMAIL = 'userAccounts/UPDATE_EMAIL',
  UPDATE_PASSWORD = 'userAccounts/UPDATE_PASSWORD',
  SAVE_LOGIN_NAME = 'userAccounts/SAVE_LOGIN_NAME',
  SAVE_NAME_INFORMATION = 'userAccount/SAVE_NAME_INFORMATION',
  SAVE_EMAIL = 'userAccounts/SAVE_EMAIL',
  SAVE_PHONE = 'userAccounts/SAVE_PHONE',
  SAVE_SECURITY_QUESTIONS = 'userAccount/SAVE_SECURITY_QUESTIONS',
  CHANGE_MOBILE_PHONE = 'userAccounts/CHANGE_MOBILE_PHONE',
  SAVE_PASSWORD = 'userAccounts/SAVE_PASSWORD',
  ACCEPT_TOS = 'userAccount/ACCEPT_TOS',
  GET_USER_PROFILE = 'userAccount/GET_USER_PROFILE',
  GET_SECURITY_QUESTIONS = 'userAccount/GET_SECURITY_QUESTIONS',
  CHANGE_USER_PROFILE = 'userAccount/CHANGE_USER_PROFILE',
  CHANGE_EMAIL = 'userAccount/CHANGE_EMAIL',
  CHANGE_SECURITY_QUESTIONS = 'userAccount/CHANGE_SECURITY_QUESTIONS',
  CHANGE_SECURITY_QUESTION_FORM = 'userAccount/CHANGE_SECURITY_QUESTION_FORM',
  UPDATE_DEFAULT_AGENCY_ASSOCIATION = 'UPDATE_DEFAULT_AGENCY_ASSOCIATION',
  CHANGE_ERROR = 'userAccount/CHANGE_ERROR',
}

export const saveSecurityQuestions = (
  userId: string,
  securityQuestionForm: UserSecurityQuestions,
  encryptedIdentifier?: string
) =>
  createAction(UserAccountActionType.SAVE_SECURITY_QUESTIONS, {
    userId,
    securityQuestionForm,
    encryptedIdentifier,
  });
export const changeSecurityQuestions = (questions: Async<SecurityQuestionPayload[]>) =>
  createAction(UserAccountActionType.CHANGE_SECURITY_QUESTIONS, questions);
export const getSecurityQuestions = () => createAction(UserAccountActionType.GET_SECURITY_QUESTIONS);
export const changeEmail = (email: Async<Either<Email, Email>>) =>
  createAction(UserAccountActionType.CHANGE_EMAIL, email);
export const getUserProfile = (userId: string) => createAction(UserAccountActionType.GET_USER_PROFILE, {userId});
export const changeSecurityQuestionForm = (securityQuestionForm: Async<UserSecurityQuestionForm>) =>
  createAction(UserAccountActionType.CHANGE_SECURITY_QUESTION_FORM, securityQuestionForm);
export const changeUserProfile = (userProfile: Async<UserProfilePayload>) =>
  createAction(UserAccountActionType.CHANGE_USER_PROFILE, userProfile);
export const saveNameInformation = (
  userId: string,
  nameInformation: ValidNameInformation,
  encryptedIdentifier?: string
) =>
  createAction(UserAccountActionType.SAVE_NAME_INFORMATION, {
    userId,
    nameInformation,
    encryptedIdentifier,
  });
export const changeNameInformation = (nameInformation: NameInformation) =>
  createAction(UserAccountActionType.CHANGE_NAME_INFORMATION, nameInformation);
export const acceptTermsOfService = (username: string) => createAction(UserAccountActionType.ACCEPT_TOS, {username});
export const updateLoginName = (loginName: NewUserName) =>
  createAction(UserAccountActionType.UPDATE_LOGIN_NAME, {loginName});

export const updateEmail = (email: NewEmail) => createAction(UserAccountActionType.UPDATE_EMAIL, {email});

export const updatePassword = (password: string, passwordConfirmation: string) =>
  createAction(UserAccountActionType.UPDATE_PASSWORD, {
    password: ValidPassword.create(password, passwordConfirmation),
  });

export const saveLoginName = (loginName: ValidNewUserName) =>
  createAction(UserAccountActionType.SAVE_LOGIN_NAME, {
    loginName,
  });

export const saveMobilePhone = (userId: string, phone: ValidPhoneNumber, encryptedIdentifier?: string) =>
  createAction(UserAccountActionType.SAVE_PHONE, {userId, phone, encryptedIdentifier});

export const changeMobilePhone = (mobilePhone: Async<Either<PhoneNumber, PhoneNumber>>) =>
  createAction(UserAccountActionType.CHANGE_MOBILE_PHONE, mobilePhone);

export const saveEmail = (userId: string, email: ValidEmail, encryptedIdentifier?: string) =>
  createAction(UserAccountActionType.SAVE_EMAIL, {userId, email, encryptedIdentifier});

export const savePassword = (password: Password, encryptedIdentifier?: string) =>
  createAction(UserAccountActionType.SAVE_PASSWORD, {
    encryptedIdentifier,
    password,
  });

export const updateDefaultAgencyAssociation = (id: string, encryptedIdentifier: string, isNewYork?: boolean) =>
  createAction(UserAccountActionType.UPDATE_DEFAULT_AGENCY_ASSOCIATION, {
    id,
    encryptedIdentifier,
    isNewYork,
  });

export const changeUserError = (errors: ValidationError[]) => createAction(UserAccountActionType.CHANGE_ERROR, errors);

type UserAccountAction =
  | ReturnType<typeof changeSecurityQuestions>
  | ReturnType<typeof changeMobilePhone>
  | ReturnType<typeof changeUserProfile>
  | ReturnType<typeof changeNameInformation>
  | ReturnType<typeof updateLoginName>
  | ReturnType<typeof updateEmail>
  | ReturnType<typeof updatePassword>
  | ReturnType<typeof changeEmail>
  | ReturnType<typeof savePassword>
  | ReturnType<typeof saveEmail>
  | ReturnType<typeof changeSecurityQuestionForm>
  | ReturnType<typeof saveLoginName>
  | ReturnType<typeof changeUserError>;

type UserAccountState = {
  newEmail: NewEmail;
  confirmPassword: Password;
  newLoginName: NewUserName;
  nameInformation: NameInformation;
  email: Async<Either<Email, Email>>;
  questions: Async<SecurityQuestionPayload[]>;
  mobilePhone: Async<Either<PhoneNumber, PhoneNumber>>;
  userProfile: Async<UserProfilePayload>;
  securityQuestionForm: Async<UserSecurityQuestionForm>;
  validationError: ValidationError[];
};

export const DEFAULT_STATE: UserAccountState = {
  confirmPassword: new UnvalidatedPassword('', ''),
  newEmail: new UnvalidatedNewEmail('', ''),
  newLoginName: new UnvalidatedNewUserName('', ''),
  securityQuestionForm: new Empty(),
  questions: new Empty(),
  userProfile: new Empty(),
  mobilePhone: new Empty(),
  email: new Empty(),
  nameInformation: new UnchangedNameInformation(new EmptyString(), new EmptyString()),
  validationError: [],
};

export default (state: UserAccountState = DEFAULT_STATE, action: UserAccountAction): UserAccountState => {
  switch (action.type) {
    case UserAccountActionType.CHANGE_SECURITY_QUESTIONS:
      return {...state, questions: action.payload};
    case UserAccountActionType.CHANGE_MOBILE_PHONE:
      return {...state, mobilePhone: action.payload};
    case UserAccountActionType.CHANGE_EMAIL:
      return {...state, email: action.payload};
    case UserAccountActionType.CHANGE_SECURITY_QUESTION_FORM:
      return {...state, securityQuestionForm: action.payload};
    case UserAccountActionType.CHANGE_USER_PROFILE:
      return {...state, userProfile: action.payload};
    case UserAccountActionType.CHANGE_NAME_INFORMATION:
      return {
        ...state,
        nameInformation: action.payload,
      };
    case UserAccountActionType.UPDATE_EMAIL:
      return {
        ...state,
        newEmail: action.payload.email,
      };
    case UserAccountActionType.UPDATE_LOGIN_NAME:
      return {
        ...state,
        newLoginName: action.payload.loginName,
      };
    case UserAccountActionType.UPDATE_PASSWORD:
      return {
        ...state,
        confirmPassword: action.payload.password,
      };
    case UserAccountActionType.CHANGE_ERROR:
      return {
        ...state,
        validationError: action.payload,
      };
    default:
      return state;
  }
};
